/**
 * CORRESPONDENCE AND COMMUNICATION PREFERENCES, in one vocabulary.
 *
 * Two namespaces for one fact: a Person can be reached in several ways and
 * has a say over each of them. `correspondence.*` is the chart's chronology
 * of what was said to and by a Patient on any channel, and its composer;
 * `commPrefs.*` is the leaf an account card and a chart both mount to show
 * the Person's verified addresses, their preference per channel and purpose,
 * and their notification hours.
 *
 * `message.*` is the third: the chart's register of what was sent and the
 * composer that writes it, which the same screens read in the same breath.
 *
 * The delivery words are exact and never collapsed. A provider ACCEPTING a
 * message is not the recipient HAVING it, and the surface names each state
 * by what the channel actually reported; the older email-only words
 * (`message.state.*`) are reused for the states they already name, and now
 * stand in this module beside the ones that generalized them.
 *
 * Composed into the public catalogue by `catalogue.en.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const correspondence = {
  'correspondence.refusal.sourceInactive': 'The event was closed or its recipient changed before sending.',
  'correspondence.refusal.protectedPayloadUnavailable': 'The protected document link could not be prepared. Contact the clinic administrator.',
  // The chronology.
  'correspondence.listLabel': 'Correspondence with this patient',
  'correspondence.empty': 'No correspondence yet',
  'correspondence.emptyDetail':
    'Messages exchanged with this patient on email, Telegram or WhatsApp will be listed here.',
  'correspondence.truncated':
    'The clinic keeps only its newest {count} conversations in this index, so older ones may be missing here.',
  'correspondence.refresh': 'Refresh',

  'correspondence.direction.inbound': 'Received',
  'correspondence.direction.outbound': 'Sent',
  'correspondence.actorKind.provider': 'via the provider',
  'correspondence.actorKind.service': 'automatic',

  'correspondence.channel.email': 'Email',
  'correspondence.channel.telegram': 'Telegram',
  'correspondence.channel.whatsapp': 'WhatsApp',
  'correspondence.channel.inApp': 'In-app',

  'correspondence.to': 'To {name} at {address}',
  'correspondence.from': 'From {name} at {address}',
  'correspondence.endpointStatus': 'Endpoint {status} when recorded',
  'correspondence.purpose': 'Purpose: {purpose}',
  'correspondence.noSubject': 'No subject',
  'correspondence.mediaOnly': 'Carried an attachment without text. Attachments are not shown here.',
  'correspondence.webAppButton': 'Offered the button “{text}”',

  // The five states the email projection never had a word for.
  'correspondence.state.accepted': 'Sent',
  'correspondence.state.delivered': 'Delivered',
  'correspondence.state.undelivered': 'Not delivered',
  'correspondence.state.read': 'Read',
  'correspondence.state.received': 'Received',
  'correspondence.retrying': 'Retrying after a failed attempt',

  'correspondence.refusal.connectorMismatch': 'The messaging connection is configured incorrectly. Contact the clinic administrator.',
  'correspondence.refusal.endpointAmbiguous': 'More than one recipient address is configured. Ask the clinic administrator to check the contact details.',
  'correspondence.refusal.serviceUnavailable': 'The messaging service is unavailable. Delivery has not been confirmed.',
  'correspondence.refusal.telegramRejected': 'Telegram refused the message. Check that the patient has started the bot and has not blocked it.',
  'correspondence.refusal.messageInvalid': 'The channel cannot send this message. Check its length and content.',
  'correspondence.refusal.unknown': 'The channel could not send the message. Contact the clinic administrator.',
  'correspondence.refusal.noSelectedChannels': 'No communication channel is selected for this recipient.',
  'correspondence.refusal.routeMissing': 'no route is configured for this channel',
  'correspondence.refusal.routeAmbiguous': 'more than one route is configured for this channel',
  'correspondence.refusal.routeChanged': 'the route changed while the message was being recorded',
  'correspondence.refusal.endpointUnverified': 'the endpoint was not verified',
  'correspondence.refusal.preferenceRequired': 'the patient has not chosen this channel',
  'correspondence.refusal.preferenceDenied': 'the patient declined this channel',
  'correspondence.refusal.connectorUnavailable': 'the connector was unavailable',
  'correspondence.refused': 'Refused: {reason}',
  'correspondence.refusedCode': 'Refused with code {code}',

  // The provider's own account of one message.
  'correspondence.historyShow': 'Delivery history',
  'correspondence.historyHide': 'Hide delivery history',
  'correspondence.historyLabel': 'Delivery history of the message of {time}',
  'correspondence.historyEmpty': 'The provider has not reported anything about this message yet.',
  'correspondence.historyRefused': 'You do not have access to the delivery history.',
  'correspondence.historyFailed': 'The delivery history could not be loaded.',
  'correspondence.attempt': 'Attempt {n}',
  'correspondence.attemptUnknown': 'Provider report',
  'correspondence.eventSimulated': 'simulated',
  'correspondence.eventDetail': '{state} — {detail}',

  'correspondence.provenance.whatsAppProvisional': 'WhatsApp identity not yet confirmed',
  'correspondence.provenance.whatsAppVerified': 'WhatsApp identity confirmed',
  'correspondence.provenance.lead': 'Started as an enquiry before this chart existed',

  // Writing back.
  'correspondence.compose': 'Write a message',
  'correspondence.reply': 'Reply',
  'correspondence.replyTitle': 'Reply on {channel}',
  'correspondence.replyingTo': 'Replying in the {channel} conversation to the message of {time}.',
  'correspondence.replyCancel': 'Write a new message instead',
  'correspondence.channelLabel': 'Channel',
  'correspondence.channelHint':
    'Only channels with a verified endpoint, the patient’s preference and an enabled route are offered.',
  'correspondence.channelWithheld': '{channel}: {reason}',
  'correspondence.withheld.noPermission': 'you cannot send on this channel',
  'correspondence.withheld.noEndpoint': 'no verified endpoint',
  'correspondence.withheld.notAllowed': 'not chosen by the patient',
  'correspondence.withheld.denied': 'declined by the patient',
  'correspondence.withheld.noRoute': 'no enabled route',
  'correspondence.withheld.unknown': 'the endpoint and preference could not be checked',
  'correspondence.noChannel': 'No channel is available for this patient right now.',
  'correspondence.needChannel': 'Choose a channel before sending.',
  'correspondence.chatSubjectNote': 'On Telegram and WhatsApp the subject goes out as the first line.',
  'correspondence.send': 'Send',
  'correspondence.sending': 'Sending…',
  'correspondence.recorded': 'Recorded and handed to {channel}.',
  'correspondence.recordedRefused': 'Recorded, but delivery was refused: {reason}.',
  'correspondence.sendMismatch':
    'This conversation no longer matches the chosen channel. Your text is kept — choose the channel again.',
  'correspondence.sendStale':
    'The channel is no longer available. Your text is kept — choose another channel.',
  'correspondence.sendEndpointGone':
    'That endpoint is no longer available. Your text is kept — choose another channel.',
  /* The register and the composer ----------------------------------------
     One namespace for both entry points: the chart's register and the
     dashboard's composer are one act with two doors, and two vocabularies for
     one act is how the same button ends up worded two ways.

     It reads here rather than on the `patients` surface because one panel's
     words are one module's business: `PatientCorrespondenceSection`,
     `CorrespondenceEntryView` and `messageParts` each drew from BOTH
     namespaces, so half of what one screen says was maintained a directory
     away from the other half — which is how `message.state.*` came to be the
     delivery vocabulary of a module that did not hold it. */
  'message.sectionTitle': 'Messages',
  'message.tableLabel': 'Messages sent to this patient',
  'message.readRefused': 'You do not have access to this patient’s messages.',
  'message.readFailed': 'The messages could not be loaded.',
  'message.empty': 'Nothing has been sent yet',
  'message.emptyDetail':
    'Messages written to this patient will be listed here.',
  'message.column.subject': 'Subject',
  'message.column.author': 'Written by',
  'message.column.sent': 'Sent',
  'message.column.state': 'Delivery',
  'message.column.addressedTo': 'Addressed to {address}',
  'message.composerTitle': 'Write a message',
  'message.recipientLabel': 'Recipient',
  'message.recipientPlaceholder': 'No recipient chosen',
  'message.patientLabel': 'Patient',
  'message.recipientAllGroup': 'All recipients',
  'message.recipientNoMatches': 'No recipient matches',
  'message.recipientLoadFailed': 'Recipients could not be loaded.',
  'message.noRecipientChosen': 'Choose who this message is for.',
  'message.noPatientChosenFiles': 'Choose who these files are for.',
  'message.badgeNoAddress': 'no email address on file',
  'message.subjectLabel': 'Subject',
  'message.bodyLabel': 'Message',
  'message.signatureTitle': 'Your signature',
  'message.signatureEmpty':
    'You have not prepared a signature. The message will be sent without one.',
  'message.signatureEdit': 'Edit it on your profile',
  'message.send': 'Send',
  'message.sending': 'Sending…',
  /* Refusals. Each names the thing to do about it: the three the composer can
     see itself, and the ones only the server knows. */
  'message.needRecipient': 'Choose a recipient before sending.',
  'message.needSubject': 'Write a subject before sending.',
  'message.needBody': 'Write the message before sending.',
  'message.needPatientForFiles': 'Choose a patient before adding these files.',
  'message.sendNoAddress':
    'This recipient has no email address on file, so there is nowhere to send it.',
  'message.sendInvalid':
    'The message was refused: check the subject and the text.',
  'message.sendTooLong': 'The message is too long to send.',
  'message.sendConflict':
    'This message was already sent. Edit it to send a different one.',
  'message.sendForbidden': 'You do not have permission to send messages.',
  'message.sendRecipientGone': 'That recipient is no longer available.',
  'message.sendSessionEnded': 'Your session ended. Sign in again to send.',
  'message.sendUnavailable': 'The message could not be sent. Try again.',
  'message.sendFailed': 'The message could not be sent.',
  /* WHAT THE CHANNEL DID, and the four states are never collapsed into two.
     `simulated` means the clinic has no mail channel configured and nothing
     left the building; reading it as sent is the one lie this surface must
     never tell. */
  'message.state.partial': 'Delivery results differ by channel',
  'message.noSelectedChannels':
    'Message not sent: the patient has no reachable communication channel.',
  'message.internalFallback':
    'External delivery failed. The message is available in the recipient’s internal notifications.',
  'message.channelOutcome': '{channel}: {state}',
  'message.state.pending': 'Sending…',
  'message.state.sent': 'Sent',
  'message.state.simulated': 'Not delivered — demonstration',
  'message.state.failed': 'Not sent',
  'message.outcomeUnavailable':
    'The delivery status could not be refreshed. Check the connection; do not send another copy yet.',
  'message.outcomeAddressed': 'Addressed to {name} at {address}.',
  'message.discardTitle': 'Discard this message?',
  'message.discardDetail': 'What you have written will not be kept.',
  'message.keepEditing': 'Keep writing',
  'message.discardConfirm': 'Discard',
  'message.unsavedIntent': 'You have a message that has not been sent.',

  // The Person's say over each channel, as one leaf of their record.
  'commPrefs.title': 'Communication preferences',
  'commPrefs.readRefused': 'You do not have access to this person’s communication preferences.',
  'commPrefs.readFailed': 'The communication preferences could not be loaded.',
  'commPrefs.edit': 'Edit communication preferences',
  'commPrefs.readOnly': 'You can read these preferences but not change them.',
  'commPrefs.endpointsTitle': 'Verified addresses',
  'commPrefs.endpoint.status.provisional': 'awaiting verification',
  'commPrefs.endpoint.status.verified': 'verified',
  'commPrefs.endpoint.status.revoked': 'revoked',

  'commPrefs.purpose.patientCommunications': 'Chat with the clinic',
  'commPrefs.purpose.patientMessage': 'Messages from the clinic',
  'commPrefs.purpose.treatmentCostEstimate': 'Treatment cost estimates',
  'commPrefs.purpose.onboardingVerification': 'Onboarding verification',
  'commPrefs.purpose.staffCommunications': 'Clinic communications',
  'commPrefs.purpose.taskReminder': 'Task reminders',

  'commPrefs.messageTypes': 'Messages',
  'commPrefs.blockMessages': 'Block message delivery',
  'commPrefs.messagesBlocked': 'Message delivery is blocked.',
  'commPrefs.unselectedNote':
    'Each selected message type goes by every selected channel; anything unselected is declined.',
  'commPrefs.channels': 'Channels',
  'commPrefs.channelLinkNote': 'Delivery also needs a verified address or a linked messenger.',
  'commPrefs.hours.title': 'Notification hours',
  'commPrefs.hours.detail':
    'Ordinary notifications, every day, {zone}. The window may cross midnight. Urgent events are sent at once regardless.',
  'commPrefs.hours.always': 'Any time',
  'commPrefs.hours.from': 'From',
  'commPrefs.hours.until': 'Until',
  'commPrefs.hours.window': '{from} – {until}',
  'commPrefs.hours.invalid': 'Choose different start and end times.',
  'commPrefs.incompleteSelection':
    'Select at least one message type and one channel, or clear both groups to decline all optional messages.',

  'commPrefs.saveSettings': 'Save preferences',
  'commPrefs.saving': 'Saving…',
  'commPrefs.cancel': 'Cancel',
  'commPrefs.saved': 'Saved.',
  'commPrefs.batchFailed': 'The preferences could not be saved. Check the stored preferences before trying again.',
  'commPrefs.batchConflict':
    'These preferences changed elsewhere, so this save was refused. Reload the stored preferences before choosing again.',
  'commPrefs.saveForbidden': 'You do not have permission to change communication preferences.',
  'commPrefs.saveInvalid': 'The preferences were refused: check the fields.',
  'commPrefs.saveSessionEnded': 'Your session ended. Sign in again to save.',
  'commPrefs.reloadSettings': 'Reload stored preferences',
  'commPrefs.reloadFailed': 'The preferences could not be reloaded. Your draft is kept.',
  'commPrefs.reloaded': 'The stored preferences have replaced your draft.',
  'commPrefs.unsavedIntent': 'Patient communication preferences',
  'commPrefs.staffUnsavedIntent': 'Your communication preferences',
} as const
